/**
 * Asset manifest — preloads all assets required for offline gameplay.
 *
 * Same-origin (and CORS-permissive) assets are fetched into blob Object URLs
 * so they survive connectivity drops; CDN (CloudFront) result-screen overlay
 * images are warmed into the browser's HTTP cache via <img> loads since they
 * lack CORS headers.
 */

import { MAZE_AUDIO_FILES } from '@/constants/audio';
import { getCloudFrontAssetUrl } from '@/utils/asset-utils';

// ── Public assets served from the app origin ─────────────────────────────

const PUBLIC_ASSET_URLS = [
  '/assets/mandala-vector.png',
  '/assets/hand-icon.svg',
  '/assets/pinch.png',
  '/assets/icons/stage-1/running.svg',
  '/assets/icons/stage-1/star.svg',
  '/assets/icons/stage-2/running.svg',
  '/assets/icons/stage-2/star.svg',
  '/assets/icons/stage-3/running.svg',
  '/assets/icons/stage-3/star.svg',
  '/assets/icons/stage-4/running.svg',
  '/assets/icons/stage-4/star.svg',
  '/assets/icons/stage-5/running.png',
  '/assets/icons/stage-5/star.png',
  '/assets/icons/stage-6/running.png',
  '/assets/icons/stage-6/star.png',
  '/assets/icons/stage-7/running.png',
  '/assets/icons/stage-7/star.png',
];

// ── SFX from an external CORS-permissive host — blob-fetched so Howler plays from cache ──

const AUDIO_URLS = [
  MAZE_AUDIO_FILES.move,
  MAZE_AUDIO_FILES.pickSide,
  MAZE_AUDIO_FILES.win,
  MAZE_AUDIO_FILES.lose,
];

// ── CDN result-screen overlay images — warmed into HTTP cache (no CORS) ───

const CDN_IMAGE_URLS = [
  getCloudFrontAssetUrl('stage-1/stage-1-ending.png'),
  getCloudFrontAssetUrl('stage-2/stage-2-ending.png'),
  getCloudFrontAssetUrl('stage-3/stage-3-ending.png'),
  getCloudFrontAssetUrl('stage-4/stage-4-ending.png'),
  getCloudFrontAssetUrl('stage-5/stage-5-ending.png'),
  getCloudFrontAssetUrl('stage-6/stage-6-ending.png'),
  getCloudFrontAssetUrl('stage-7/stage-7-ending.png'),
  getCloudFrontAssetUrl('end_overlay.png'),
  getCloudFrontAssetUrl('end_overlay2.png'),
  getCloudFrontAssetUrl('end_overlay5.png'),
  getCloudFrontAssetUrl('end_overlay6.png'),
  getCloudFrontAssetUrl('end_overlay7.png'),
];

// ── CDN bgm — warmed into HTTP cache the same way (no CORS) ───────────────

const CDN_AUDIO_URLS = [MAZE_AUDIO_FILES.bgm];

// ── Object URL cache ─────────────────────────────────────────────────────
// Gameplay code reads from this instead of re-requesting the original URL,
// so it never depends on the browser's HTTP cache surviving a connectivity drop.

const objectUrlCache = new Map<string, string>();

export function getPreloadedAssetUrl(originalUrl: string): string {
  return objectUrlCache.get(originalUrl) ?? originalUrl;
}

// ── Fetchers ─────────────────────────────────────────────────────────────

async function fetchAsset(url: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to preload asset: ${url} (${response.status})`);
  }
  const blob = await response.blob();
  objectUrlCache.set(url, URL.createObjectURL(blob));
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
    img.src = url;
  });
}

function preloadAudio(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.oncanplaythrough = () => resolve();
    audio.onerror = () => reject(new Error(`Failed to preload audio: ${url}`));
    audio.preload = 'auto';
    audio.src = url;
  });
}

// ── Main preloader ───────────────────────────────────────────────────────

export async function preloadAllAssets(): Promise<void> {
  // Demo/Game/Result (and the gsap/pixi/howler-heavy MazeSection they render)
  // are statically imported in AppRouter.tsx/game-page.tsx/demo-page.tsx rather
  // than React.lazy chunks, so they're already part of this same bundle — no
  // separate dynamic import needed here to warm them up.
  await Promise.all([
    ...PUBLIC_ASSET_URLS.map(fetchAsset),
    ...AUDIO_URLS.map(fetchAsset),
    ...CDN_IMAGE_URLS.map(preloadImage),
    ...CDN_AUDIO_URLS.map(preloadAudio),
  ]);
}
